import { useMemo } from 'react';
import { useRBAC } from '../contexts/RBACContext';
import type { Permission, Role } from '../types';
import RBACUtils from '../utils/rbacUtils';
import { PolicyEngine, UI_POLICIES } from '../policies/rbacPolicies';
import {
  getUserPermissions as getMultiRolePermissions,
  hasPermission as multiRoleHasPermission,
  hasAnyPermission as multiRoleHasAny,
  hasAllPermissions as multiRoleHasAll,
  getUserRoleSlugs,
} from '../utils/permissions';
import { getRefId } from '../utils/refId';

/** Anything carrying a pod's lead — `Pod.leadId` may be an id or the populated user. */
type PodLeadCarrier = { leadId?: unknown } | null | undefined;

/**
 * Custom hook for permission checking with additional utilities.
 *
 * RBAC v2: Now supports multi-role resolution via user.roles[].
 * The hook merges permissions from all assigned roles and checks
 * super-permission hierarchy (* > write > read > list).
 */
export const usePermissions = () => {
  const rbac = useRBAC();

  // Common permission patterns
  const canViewPods = () => rbac.hasPermission('pods', 'view');
  const canCreatePods = () => rbac.hasPermission('pods', 'create');
  const canEditPods = () => rbac.hasPermission('pods', 'edit');
  const canDeletePods = () => rbac.hasPermission('pods', 'delete');
  const canManagePods = () => rbac.hasPermission('pods', 'manage');
  const canAssignPodMembers = () => rbac.hasPermission('pods', 'assign_members');
  const canManagePodLeads = () => rbac.hasPermission('pods', 'manage_leads');
  const canViewPodAnalytics = () => rbac.hasPermission('pods', 'view_analytics');

  // General project permissions (apply to all project types)
  const canViewProjects = () => rbac.hasPermission('projects', 'view');
  const canCreateProjects = () => rbac.hasPermission('projects', 'create');
  const canEditProjects = () => rbac.hasPermission('projects', 'edit');
  const canDeleteProjects = () => rbac.hasPermission('projects', 'delete');

  // Type-specific project permissions
  const canViewBuildProjects = () => rbac.hasPermission('projects', 'view_build') || canViewProjects();
  const canCreateBuildProjects = () => rbac.hasPermission('projects', 'create_build') || canCreateProjects();
  const canEditBuildProjects = () => rbac.hasPermission('projects', 'edit_build') || canEditProjects();
  const canDeleteBuildProjects = () => rbac.hasPermission('projects', 'delete_build') || canDeleteProjects();

  const canViewRetainerProjects = () => rbac.hasPermission('projects', 'view_retainer') || canViewProjects();
  const canCreateRetainerProjects = () => rbac.hasPermission('projects', 'create_retainer') || canCreateProjects();
  const canEditRetainerProjects = () => rbac.hasPermission('projects', 'edit_retainer') || canEditProjects();
  const canDeleteRetainerProjects = () => rbac.hasPermission('projects', 'delete_retainer') || canDeleteProjects();
  const canUpdateRetainerUsage = () => rbac.hasPermission('projects', 'update_retainer_usage');

  const canViewClients = () => rbac.hasPermission('clients', 'view');
  const canCreateClients = () => rbac.hasPermission('clients', 'create');
  const canEditClients = () => rbac.hasPermission('clients', 'edit');
  const canDeleteClients = () => rbac.hasPermission('clients', 'delete');

  const canViewRetainers = () => rbac.hasPermission('retainers', 'view');
  const canCreateRetainers = () => rbac.hasPermission('retainers', 'create');
  const canEditRetainers = () => rbac.hasPermission('retainers', 'edit');
  const canDeleteRetainers = () => rbac.hasPermission('retainers', 'delete');

  const canViewReports = () => rbac.hasPermission('reports', 'view');
  const canCreateReports = () => rbac.hasPermission('reports', 'create');
  const canExportReports = () => rbac.hasPermission('reports', 'export');

  const canViewUsers = () => rbac.hasPermission('users', 'view');
  const canCreateUsers = () => rbac.hasPermission('users', 'create');
  const canEditUsers = () => rbac.hasPermission('users', 'edit');
  const canDeleteUsers = () => rbac.hasPermission('users', 'delete');

  const canManageSystem = () => rbac.hasPermission('system', 'manage');
  const canConfigureSystem = () => rbac.hasPermission('system', 'configure');

  // RBAC v2: Memoized multi-role derived data
  const userRoles: Role[] = useMemo(() => rbac.userRoles ?? [], [rbac.userRoles]);
  const userRoleSlugs: string[] = useMemo(() => getUserRoleSlugs(rbac.user), [rbac.user]);
  const allUserPermissions: Permission[] = useMemo(() => getMultiRolePermissions(rbac.user), [rbac.user]);

  // Multi-role permission checks (dot-notation: "resource.action")
  const checkPermission = (permString: string): boolean => multiRoleHasPermission(rbac.user, permString);
  const checkAnyPermission = (perms: string[]): boolean => multiRoleHasAny(rbac.user, perms);
  const checkAllPermissions = (perms: string[]): boolean => multiRoleHasAll(rbac.user, perms);

  // Role-based checks — now support multi-role (checks roles[] slugs + legacy role field)
  const isAdmin = () => rbac.hasRole('admin');
  const isOperations = () => rbac.hasRole('operations');
  const isPodLead = () => rbac.hasRole('pod_lead');
  const isProjectManager = () => rbac.hasRole('project_manager');
  const isAccountManager = () => rbac.hasRole('account_manager');
  const isFinance = () => rbac.hasRole('finance');
  const isRegularUser = () => rbac.hasRole('team_member');

  // Management role checks
  const isManager = () => rbac.hasAnyPermission([
    { resource: 'pods', action: 'manage' },
    { resource: 'projects', action: 'manage' },
    { resource: 'users', action: 'manage' }
  ]);

  const canManageTeam = () => rbac.hasAnyPermission([
    { resource: 'pod_assignments', action: 'edit' },
    { resource: 'pod_assignments', action: 'assign' }
  ]);

  // Complex permission checks
  const canAccessAdminPanel = () => rbac.hasAnyPermission([
    { resource: 'system', action: 'manage' },
    { resource: 'users', action: 'manage' },
    { resource: 'system', action: 'configure' }
  ]);

  const canAccessAnalytics = () => rbac.hasAnyPermission([
    { resource: 'reports', action: 'view', scope: 'global' },
    { resource: 'forecasts', action: 'view', scope: 'global' },
    { resource: 'analytics', action: 'read' }
  ]);

  const canManageFinances = () => rbac.hasAnyPermission([
    { resource: 'projects', action: 'view', scope: 'global' },
    { resource: 'reports', action: 'view', scope: 'global' },
    { resource: 'forecasts', action: 'view', scope: 'global' }
  ]);

  // Scope-specific checks
  const canEditOwnProfile = () => rbac.hasPermission('users', 'edit', 'self');
  const canViewOwnProjects = () => rbac.hasPermission('projects', 'view', 'self');
  const canViewPodProjects = () => rbac.hasPermission('projects', 'view', 'pod');
  const canViewGlobalProjects = () => rbac.hasPermission('projects', 'view', 'global');

  // Pod-specific scope checks
  const canManageOwnPods = () => rbac.hasPermission('pods', 'manage', 'self');
  const canManageAssignedPods = () => rbac.hasPermission('pods', 'manage', 'pod');
  const canManageAllPods = () => rbac.hasPermission('pods', 'manage', 'global');

  // Pod-specific permission checks. They mirror the backend gate
  // (backend/src/middleware/podAccess.ts): a GLOBAL grant works on every pod, a
  // pod-scoped grant (pod_lead) only on pods the user LEADS. Keep the two in sync —
  // the UI must not offer a control the API would answer with 403.
  const isLeadOfPod = (pod: PodLeadCarrier): boolean => {
    const leadId = getRefId(pod?.leadId);
    return Boolean(leadId) && leadId === getRefId(rbac.user);
  };

  const hasGlobalOrLeadGrant = (resource: string, action: string, pod: PodLeadCarrier): boolean =>
    rbac.hasPermission(resource, action, 'global') ||
    ((rbac.hasPermission(resource, action, 'pod') || rbac.hasPermission(resource, action, 'team')) &&
      isLeadOfPod(pod));

  /** `PUT /api/pods/:id` — gated `pods:edit`. */
  const canEditSpecificPod = (pod: PodLeadCarrier): boolean => hasGlobalOrLeadGrant('pods', 'edit', pod);

  /** `DELETE /api/pods/:id` — `pods:delete` at GLOBAL scope only (leads never delete). */
  const canDeleteSpecificPod = (): boolean => rbac.hasPermission('pods', 'delete', 'global');

  /** Pod "manage" (analytics etc.) — global `pods:manage`, or the pod's own lead. */
  const canManageSpecificPod = (pod: PodLeadCarrier): boolean => hasGlobalOrLeadGrant('pods', 'manage', pod);

  /** Member add/remove (`/api/pod-assignments/*`, `/api/pods/:id/assignments`) — gated `pod_assignments:assign`. */
  const canAssignMembersToSpecificPod = (pod: PodLeadCarrier): boolean =>
    hasGlobalOrLeadGrant('pod_assignments', 'assign', pod);

  // Utility functions
  const getPermissionLevel = (resource: string, action: string): 'none' | 'self' | 'pod' | 'project' | 'client' | 'global' => {
    if (rbac.hasPermission(resource, action, 'global')) return 'global';
    if (rbac.hasPermission(resource, action, 'client')) return 'client';
    if (rbac.hasPermission(resource, action, 'project')) return 'project';
    if (rbac.hasPermission(resource, action, 'pod')) return 'pod';
    if (rbac.hasPermission(resource, action, 'self')) return 'self';
    return 'none';
  };

  const hasMinimumRole = (minimumRole: string): boolean => {
    const roleHierarchy = [
      'team_member',
      'account_manager',
      'finance',
      'project_manager',
      'pod_lead',
      'operations',
      'admin'
    ];

    const userRoleSlug = rbac.user?.roles?.[0]?.slug || '';
    const userRoleIndex = roleHierarchy.indexOf(userRoleSlug);
    const minimumRoleIndex = roleHierarchy.indexOf(minimumRole);

    return userRoleIndex >= minimumRoleIndex;
  };

  const canPerformAction = (resource: string, action: string, scope?: string): {
    allowed: boolean;
    reason?: string;
  } => {
    if (rbac.isAdmin) {
      return { allowed: true };
    }

    if (!rbac.user) {
      return { allowed: false, reason: 'User not authenticated' };
    }

    const hasPermission = rbac.hasPermission(resource, action, scope);
    
    if (!hasPermission) {
      return { 
        allowed: false, 
        reason: `Insufficient permissions for ${action} on ${resource}${scope ? ` (${scope})` : ''}` 
      };
    }

    return { allowed: true };
  };

  // Policy-based checks
  const canAccessNavigation = (section: keyof typeof UI_POLICIES.NAVIGATION) => {
    const requiredPermissions = UI_POLICIES.NAVIGATION[section];
    return rbac.hasAnyPermission(requiredPermissions);
  };

  const canUseButton = (button: keyof typeof UI_POLICIES.BUTTONS) => {
    const requiredPermissions = UI_POLICIES.BUTTONS[button];
    return rbac.hasAnyPermission(requiredPermissions);
  };

  const canAccessForm = (form: keyof typeof UI_POLICIES.FORMS) => {
    const requiredPermissions = UI_POLICIES.FORMS[form];
    return rbac.hasAllPermissions(requiredPermissions);
  };

  // Feature access checks
  const getAccessibleFeatures = () => {
    return PolicyEngine.getAccessibleFeatures(rbac.user?.roles?.[0]?.slug || '');
  };

  const hasFeatureAccess = (feature: string) => {
    return getAccessibleFeatures().includes(feature);
  };

  // Utility functions using RBACUtils
  const getPermissionsSummary = () => {
    return RBACUtils.getPermissionsSummary(rbac.permissions, rbac.user);
  };

  const canAccessResource = (
    resource: string,
    action: string,
    resourceId?: string,
    context?: { podId?: string; projectId?: string; clientId?: string }
  ) => {
    return RBACUtils.canAccessResource(
      rbac.permissions,
      rbac.user,
      resource,
      action,
      resourceId,
      context
    );
  };

  return {
    // Core RBAC functions
    ...rbac,

    // RBAC v2: Multi-role data and checks
    userRoles,
    userRoleSlugs,
    allUserPermissions,
    checkPermission,      // e.g. checkPermission('projects.list')
    checkAnyPermission,   // e.g. checkAnyPermission(['projects.list', 'pods.view'])
    checkAllPermissions,  // e.g. checkAllPermissions(['projects.list', 'projects.edit'])

    // Resource-specific permissions
    canViewPods,
    canCreatePods,
    canEditPods,
    canDeletePods,
    canManagePods,
    canAssignPodMembers,
    canManagePodLeads,
    canViewPodAnalytics,
    canViewProjects,
    canCreateProjects,
    canEditProjects,
    canDeleteProjects,

    // Type-specific project permissions
    canViewBuildProjects,
    canCreateBuildProjects,
    canEditBuildProjects,
    canDeleteBuildProjects,
    canViewRetainerProjects,
    canCreateRetainerProjects,
    canEditRetainerProjects,
    canDeleteRetainerProjects,
    canUpdateRetainerUsage,
    canViewClients,
    canCreateClients,
    canEditClients,
    canDeleteClients,
    canViewRetainers,
    canCreateRetainers,
    canEditRetainers,
    canDeleteRetainers,
    canViewReports,
    canCreateReports,
    canExportReports,
    canViewUsers,
    canCreateUsers,
    canEditUsers,
    canDeleteUsers,
    canManageSystem,
    canConfigureSystem,

    // Role checks
    isAdmin,
    isOperations,
    isPodLead,
    isProjectManager,
    isAccountManager,
    isFinance,
    isRegularUser,
    isManager,
    canManageTeam,

    // Complex checks
    canAccessAdminPanel,
    canAccessAnalytics,
    canManageFinances,

    // Scope-specific checks
    canEditOwnProfile,
    canViewOwnProjects,
    canViewPodProjects,
    canViewGlobalProjects,
    canManageOwnPods,
    canManageAssignedPods,
    canManageAllPods,
    canEditSpecificPod,
    canDeleteSpecificPod,
    canManageSpecificPod,
    canAssignMembersToSpecificPod,
    isLeadOfPod,

    // Policy-based checks
    canAccessNavigation,
    canUseButton,
    canAccessForm,
    hasFeatureAccess,
    getAccessibleFeatures,

    // Utility functions
    getPermissionLevel,
    hasMinimumRole,
    canPerformAction,
    getPermissionsSummary,
    canAccessResource,
  };
};

export default usePermissions;
